import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import ClientButton from '../../../components/ClientButton';
import { addNewServiceAddressesApi, updateServiceAddressesApi } from '../../../redux/Reducers/ServiceAddressesReducer';
import { toast } from 'react-hot-toast';
import { AddressAutofill } from '@mapbox/search-js-react';
import { isEmpty, isNull } from 'lodash';
import Loader from '../../../components/Loader';
import Button from '../../../components/Button';
const MAPBOX_API_TOKEN =
  import.meta.env.VITE_TENANT_NAME === 'laundrycare'
    ? import.meta.env.VITE_LAUNDRYCARE_MAPBOX_API_TOKEN
    : import.meta.env.VITE_TENANT_NAME === 'pushlaundry'
    ? import.meta.env.VITE_PUSHLAUNDRY_MAPBOX_API_TOKEN
    : '';
export default function SingleAddress({ address, index, showModal, setModalOpen, type = 'new' }) {
  const dispatch = useDispatch();
  const [addressData, setAddressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isInactive = address?.status === 'INACTIVE';
  const nameField = useRef(null);
  const address1Field = useRef(null);
  const address2Field = useRef(null);
  const cityField = useRef(null);
  const stateField = useRef(null);
  const zipField = useRef(null);

  const validateAddressFn = (address) => {
    const fields = ['name', 'address_line_1', 'city', 'state', 'postal_code'];
    for (const field of fields) {
      if (!address[field] || address[field] === '') {
        toast.error(`Please enter a ${field.replace('_', ' ')}`);
        setAddressData((prev) => ({ ...prev, [field]: null }));
        return false;
      }
      if (field === 'postal_code' && address[field].length != 5) {
        toast.error(`Please enter a valid ${field.replace('_', ' ')}`);
        setAddressData((prev) => ({ ...prev, [field]: null }));
        return false;
      }
    }
    return true;
  };

  const handleSubmitAddress = (e) => {
    e.preventDefault();
    const payload = {
      name: nameField.current.value,
      address_line_1: address1Field.current.value,
      address_line_2: address2Field.current.value,
      city: cityField.current.value,
      state: stateField.current.value,
      postal_code: zipField.current.value,
    };
    if (validateAddressFn(payload)) {
      setLoading(true);
      const create = dispatch(updateServiceAddressesApi(payload, address?.id));
      create.then((res) => {
        if ([200, 201, 204].includes(res?.status)) {
          setLoading(false);
          showModal(e, true, address?.id, 'update');
        } else if (res?.status === 400) {
          if (!isEmpty(res?.data)) {
            showModal(e, true, res, 'orders', address, payload);
          } else if (res?.status == 400 && res?.type == 'Bad Request Error') {
            toast.error(res?.message || 'Something went wrong');
          }
          setLoading(false);
        } else {
          setLoading(false);
          toast.error(res?.message || 'Something went wrong');
        }
      });
    }
  };

  const addressChangeFn = (data) => {
    setAddressData((prev) => ({
      ...prev,
      address_line_1: data.features[0].properties.address_line1,
      address_line_2: data.features[0].properties.address_line2,
      city: data.features[0].properties.address_level2,
      state: data.features[0].properties.address_level1,
      postal_code: data.features[0].properties.postcode,
    }));
  };

  useEffect(() => {
    setAddressData(address);
  }, [address]);

  return (
    <div>
      <div className='z-0 mb-4 rounded-lg bg-white ' key={index}>
        {type == 'new' && (
          <header className='flex-row items-center px-5 pt-4 sm:px-6'>
            <h5 className='go-textblackmedium-16 my-1 flex-1'>Service Address</h5>
          </header>
        )}
        {loading ? (
          <Loader text='processing...' classes='h-40' />
        ) : (
          <form onSubmit={handleSubmitAddress}>
            <div className='grid gap-5 px-5 pt-4 pb-6 md:grid-cols-3'>
              <div>
                <div>
                  <label className='go-label-16 mb-2 block' htmlFor='name'>
                    Label
                  </label>
                  <input
                    ref={nameField}
                    name='name'
                    className={`go-inputtext-16 ${isNull(addressData?.name) ? 'border-red-500' : ''} ${
                      isInactive ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''
                    }`}
                    type='text'
                    defaultValue={addressData?.name}
                    onChange={(e) => setAddressData((prev) => ({ ...prev, name: e.target.value }))}
                    disabled={isInactive}
                  />
                </div>
              </div>
              <div>
                <div>
                  <label className='go-label-16 mb-2 block' htmlFor='address_line_1'>
                    Street Address
                  </label>
                  <AddressAutofill
                    accessToken={MAPBOX_API_TOKEN}
                    options={{
                      language: 'en',
                      country: 'US',
                    }}
                    onRetrieve={addressChangeFn}
                  >
                    <input
                      ref={address1Field}
                      name='address_line_1'
                      className={`go-inputtext-16 ${isNull(addressData?.address_line_1) ? 'border-red-500' : ''} ${
                        isInactive ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''
                      }`}
                      type='text'
                      placeholder='Address'
                      autoComplete='address-line1'
                      defaultValue={addressData?.address_line_1}
                      disabled={isInactive}
                    />
                  </AddressAutofill>
                </div>
              </div>

              <div>
                <div>
                  <label className='go-label-16 mb-2 block' htmlFor='address_line_2'>
                    Apartment or suite number
                  </label>
                  <input
                    ref={address2Field}
                    name='address_line_2'
                    className={`go-inputtext-16 ${isInactive ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''}`}
                    type='text'
                    placeholder='Apartment number'
                    autoComplete='address-line2'
                    defaultValue={addressData?.address_line_2}
                    disabled={isInactive}
                  />
                </div>
              </div>
              <div>
                <div>
                  <label className='go-label-16 mb-2 block' htmlFor='city'>
                    City
                  </label>
                  <input
                    ref={cityField}
                    name='city'
                    className={`go-inputtext-16 ${isNull(addressData?.city) ? 'border-red-500' : ''} ${
                      isInactive ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''
                    }`}
                    type='text'
                    placeholder='City'
                    autoComplete='address-level2'
                    defaultValue={addressData?.city}
                    disabled={isInactive}
                  />
                </div>
              </div>
              <div className='flex space-x-2'>
                <div className='flex-1'>
                  <div>
                    <label className='go-label-16 mb-2 block' htmlFor='state'>
                      State
                    </label>
                    <input
                      ref={stateField}
                      name='state'
                      className={`go-inputtext-16 ${isNull(addressData?.state) ? 'border-red-500' : ''} ${
                        isInactive ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''
                      }`}
                      type='text'
                      placeholder='State'
                      autoComplete='address-level1'
                      defaultValue={addressData?.state}
                      disabled={isInactive}
                    />
                  </div>
                </div>
                <div className='flex-1'>
                  <div>
                    <label className='go-label-16 mb-2 block' htmlFor='postal_code'>
                      Zip Code
                    </label>
                    <input
                      ref={zipField}
                      name='postal_code'
                      className={`go-inputtext-16 ${isNull(addressData?.postal_code) ? 'border-red-500' : ''} ${
                        isInactive ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''
                      }`}
                      type='number'
                      placeholder='Postcode'
                      autoComplete='postal-code'
                      defaultValue={addressData?.postal_code}
                      maxLength={5}
                      disabled={isInactive}
                    />
                  </div>
                </div>
              </div>
            </div>
            {!isInactive && (
              <div className='flex space-x-4 p-5'>
                <Button variant='primary-dark' text='Save' classes='w-full xs:w-56' type='submit' />
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
